/**
 * The house action dialog (M5 P5e #8, UX_SPEC 5) rebuilt on shadcn's Dialog.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * SAME CONTRACT as nav/ActionDialog.tsx (open, title, wide, onClose, children), so a
 * screen migrates by changing one import. Radix supplies the focus trap, Escape, the
 * overlay click, the portal and the scroll lock; the house keeps two behaviours:
 * - the first FIELD takes focus on open, never the close button (M5 P6 #4);
 * - the parent decides when it closes (it closes only after a submission resolves).
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { strings } from "@/brand/strings";
import { cn } from "@/lib/utils";

const FIRST_FIELD =
  "input:not([type='hidden']):not([type='file']), select, textarea, [role='checkbox'], [role='radio']";

export function ActionDialog({
  open,
  title,
  onClose,
  children,
  wide = false,
}: {
  readonly open: boolean;
  readonly title: string;
  /** A long form (the create-project registration) takes a wider sheet. */
  readonly wide?: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent
        data-testid="action-dialog"
        overlayTestId="action-dialog-overlay"
        closeTestId="action-dialog-close"
        closeLabel={strings.common.close}
        aria-describedby={undefined}
        className={cn(wide && "max-w-[48rem]")}
        onOpenAutoFocus={(event) => {
          const node = event.currentTarget as HTMLElement | null;
          const field = node?.querySelector<HTMLElement>(FIRST_FIELD);
          if (field) {
            event.preventDefault();
            field.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}
